import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Bell, Eye, CheckCheck, X, FileText, User, Volume2, VolumeX } from "lucide-react";

// -------------- sound (Web Audio, no asset required) --------------
let _audioCtx = null;
function playDing() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!_audioCtx) _audioCtx = new AC();
    if (_audioCtx.state === "suspended") _audioCtx.resume();
    const t0 = _audioCtx.currentTime;
    const notes = [
      { f: 988, t: 0, d: 0.16, g: 0.32 }, // B5
      { f: 1319, t: 0.14, d: 0.30, g: 0.28 }, // E6
    ];
    notes.forEach(({ f, t, d, g }) => {
      const osc = _audioCtx.createOscillator();
      const gain = _audioCtx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(f, t0 + t);
      gain.gain.setValueAtTime(0.0001, t0 + t);
      gain.gain.exponentialRampToValueAtTime(g, t0 + t + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + t + d);
      osc.connect(gain).connect(_audioCtx.destination);
      osc.start(t0 + t);
      osc.stop(t0 + t + d + 0.02);
    });
  } catch (_) {}
}

const SEEN_KEY = "pp_seen_pending_ids_v1";
const SOUND_KEY = "pp_notification_sound_v1";

function loadSeen() {
  try { return new Set(JSON.parse(sessionStorage.getItem(SEEN_KEY) || "[]")); } catch (_) { return new Set(); }
}
function saveSeen(set) {
  try { sessionStorage.setItem(SEEN_KEY, JSON.stringify([...set])); } catch (_) {}
}

// -------------- Notification card rendered inside sonner toast --------------
function ApprovalToast({ job, onApprove, onReview, onReject, onDismiss }) {
  return (
    <div className="w-[360px] sm:w-[400px] rounded-2xl bg-white border-2 border-orange-300 shadow-2xl overflow-hidden" data-testid={`approval-toast-${job.id}`}>
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-2.5 flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-white/25 backdrop-blur grid place-items-center">
          <Bell className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-90">New order · Awaiting approval</div>
          <div className="text-sm font-display font-bold truncate">{job.job_number}</div>
        </div>
        <button onClick={onDismiss} className="w-7 h-7 rounded-full hover:bg-white/20 grid place-items-center" data-testid="toast-close">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="p-4 space-y-2.5">
        <div className="flex items-center gap-2 text-sm">
          <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="text-slate-700 font-medium truncate">
            {job.customer_name || "Walk-in customer"}
            {job.customer_mobile ? <span className="text-slate-400 font-mono text-xs"> · {job.customer_mobile}</span> : null}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="text-slate-700 truncate">
            {job.total_pages} pages · {(job.files || []).length} file{(job.files || []).length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="flex items-center justify-between pt-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Payment</div>
          <div className="text-lg font-display font-black text-orange-600">₹{job.amount}
            <span className="ml-1.5 text-[10px] font-mono uppercase tracking-widest text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
              {job.payment_status === "cod" ? "Cash" : "Paid"}
            </span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5 p-3 pt-0">
        <Button size="sm" onClick={onApprove} className="bg-emerald-500 hover:bg-emerald-600 text-white h-9 text-xs font-semibold" data-testid={`toast-approve-${job.id}`}>
          <CheckCheck className="w-3.5 h-3.5 mr-1" />Approve
        </Button>
        <Button size="sm" onClick={onReview} variant="outline" className="h-9 text-xs font-semibold border-slate-300" data-testid={`toast-review-${job.id}`}>
          <Eye className="w-3.5 h-3.5 mr-1" />Review
        </Button>
        <Button size="sm" onClick={onReject} variant="outline" className="h-9 text-xs font-semibold border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`toast-reject-${job.id}`}>
          <X className="w-3.5 h-3.5 mr-1" />Reject
        </Button>
      </div>
    </div>
  );
}

// -------------- Reject confirmation dialog --------------
function RejectConfirmDialog({ job, open, onOpenChange, onConfirmed }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  if (!job) return null;
  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/store/jobs/${job.id}/reject`, { reason: reason.trim() });
      toast.success(`${job.job_number} rejected`);
      onConfirmed?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reject");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reject-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <X className="w-5 h-5 text-red-500" />Reject order {job.job_number}?
          </DialogTitle>
          <DialogDescription>
            This can't be undone. The customer's cash order will be cancelled and won't be printed. Add a short note so you remember why.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="reject-reason">Reason (optional)</Label>
          <textarea
            id="reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Customer walked away without paying"
            className="w-full min-h-[80px] rounded-md border border-slate-200 p-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
            data-testid="reject-reason"
            maxLength={200}
          />
          <div className="text-[10px] text-slate-400 text-right">{reason.length}/200</div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="reject-cancel">
            Keep order
          </Button>
          <Button onClick={confirm} disabled={busy} className="bg-red-500 hover:bg-red-600" data-testid="reject-confirm">
            {busy ? "Rejecting…" : "Yes, reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------- Watcher --------------
export default function PendingApprovalWatcher() {
  const nav = useNavigate();
  const seenRef = useRef(loadSeen());
  const firstRunRef = useRef(true);
  const [rejectJob, setRejectJob] = useState(null);
  const [soundOn, setSoundOn] = useState(() => {
    try { return sessionStorage.getItem(SOUND_KEY) !== "off"; } catch (_) { return true; }
  });

  // Prime the AudioContext on the FIRST user gesture so sound works when the toast fires later.
  useEffect(() => {
    const unlock = () => {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!_audioCtx && AC) _audioCtx = new AC();
        if (_audioCtx && _audioCtx.state === "suspended") _audioCtx.resume();
      } catch (_) {}
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
    window.addEventListener("click", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    window.addEventListener("touchstart", unlock, { once: true });
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, []);

  const approve = useCallback(async (job, toastId) => {
    try {
      await api.post(`/store/jobs/${job.id}/approve`);
      toast.success(`${job.job_number} approved — sending to printer`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to approve");
    } finally {
      toast.dismiss(toastId);
    }
  }, []);

  const review = useCallback((job, toastId) => {
    toast.dismiss(toastId);
    nav(`/store/jobs?job=${job.id}`);
  }, [nav]);

  const showApprovalToast = useCallback((job) => {
    if (soundOn) playDing();
    toast.custom((t) => (
      <ApprovalToast
        job={job}
        onApprove={() => approve(job, t)}
        onReview={() => review(job, t)}
        onReject={() => { toast.dismiss(t); setRejectJob(job); }}
        onDismiss={() => toast.dismiss(t)}
      />
    ), { duration: 60_000 });
  }, [approve, review, soundOn]);

  // Polling
  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const r = await api.get(`/store/jobs`, { params: { status_group: "open" } });
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        const pending = list.filter((j) => j.status === "pending_approval");
        const currentIds = new Set(pending.map((j) => j.id));
        if (firstRunRef.current) {
          // On first load, mark everything already-visible as seen so we don't spam-toast.
          currentIds.forEach((id) => seenRef.current.add(id));
          saveSeen(seenRef.current);
          firstRunRef.current = false;
        } else {
          const fresh = pending.filter((j) => !seenRef.current.has(j.id));
          fresh.forEach((j) => {
            seenRef.current.add(j.id);
            showApprovalToast(j);
          });
          // Prune ids that are no longer pending (approved / rejected elsewhere)
          [...seenRef.current].forEach((id) => { if (!currentIds.has(id)) seenRef.current.delete(id); });
          if (fresh.length) saveSeen(seenRef.current);
        }
      } catch (_) { /* silent */ }
      if (!stop) setTimeout(poll, 8_000);
    };
    poll();
    return () => { stop = true; };
  }, [showApprovalToast]);

  const toggleSound = () => {
    setSoundOn((prev) => {
      const next = !prev;
      try { sessionStorage.setItem(SOUND_KEY, next ? "on" : "off"); } catch (_) {}
      toast.message(next ? "Alert sound enabled" : "Alert sound muted");
      if (next) playDing();
      return next;
    });
  };

  return (
    <>
      {/* Sound toggle — small, fixed-position, avoids nav clutter */}
      <button
        type="button"
        onClick={toggleSound}
        title={soundOn ? "Mute new-order alerts" : "Enable new-order alerts"}
        className="fixed bottom-4 right-4 z-40 w-11 h-11 rounded-full bg-white border border-slate-200 shadow-lg hover:shadow-xl grid place-items-center text-slate-600 hover:text-orange-600 transition"
        data-testid="toggle-alert-sound"
      >
        {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4 text-slate-300" />}
      </button>
      <RejectConfirmDialog
        job={rejectJob}
        open={!!rejectJob}
        onOpenChange={(o) => { if (!o) setRejectJob(null); }}
        onConfirmed={() => setRejectJob(null)}
      />
    </>
  );
}
